import create from 'zustand';
import wretch from 'wretch';
import FormDataAddon from 'wretch/addons/formData';
import AbortAddon from 'wretch/addons/abort';
import {AuthError} from '../errors/authError';
import {AuthErrorTypeEnum} from './authStore';

export interface Identifier {
    value: string;
}

export interface Product {
    id: number;
    sku: string;
    name: string;
    imageUrl: string | null;
}

export interface OrderItem {
    id: number;
    salable: Product;
    description: string;
    identifier: Identifier;
    allowShipment: boolean;
    qty: number;
    qtyCanceled: number;
    qtyInvoiced: number;
    qtyRefunded: number;
    qtyShipped: number;
}

export interface Order {
    id: number;
    reference: string;
    identifier: Identifier;
    orderItems: Array<OrderItem>;
}

export interface SupportCaseItem {
    id: number;
    identifier: Identifier;
    state: string;
    status: string;
    qty: number | null;
    productState: string;
    description: string;
    solution: Solution;
    requestedResolution: SolutionType | null;
}

export interface SupportCase {
    id: number;
    state: 'draft' | 'pending_action' | 'new' | 'closed';
    order: Order;
    identifier: Identifier;
    items: Array<SupportCaseItem>;
    solution: Solution | null;
    requestedResolution: SolutionType | null;
}

export interface RMA {
    state: 'requested';
}

export type SupportCaseListResponse = Array<{
    supportCase: SupportCase;
    identifier: string;
    rma?: RMA;
}>;

export interface Answer {
    id: number;
    label: string;
    nextQuestion: Omit<Question, 'answers'> | null;
}

export interface SolutionType {
    label: string;
    description: string;
    explanation: string;
    action: 'rma' | 'ticket' | 'advice';
}

export interface Solution {
    id: number;
    type: SolutionType;
    label: string;
    perItem: boolean;
    requireItems: boolean;
    description: string;
    explanation: string;
    finalSolution: boolean;
    requireUpload: boolean;
    requireDescription: boolean;
    uploadDescription: string | null;
    descriptionDescription: string | null;
}

export interface Question {
    id: number;
    label: string;
    description: string;
    answers: Array<Answer>;
    perItem: boolean;
    requireItems: boolean;
    requireSingleItem: boolean;
}

export interface RootQuestionsState {
    type: 'rootQuestions';
    questions: Array<Question>;
    answer: Answer | null;
}

export interface QuestionState {
    type: 'question';
    question: Question;
    answer: Answer | null;
}

export interface ItemsSelectionState {
    type: 'itemsSelection';
    items: Array<OrderItem>;
    qtyRequired: boolean;
}

export interface MultiItemQuestionState {
    type: 'multiItemQuestion';
    questions: Record<string, Question>;
    answers: Record<string, Answer> | null;
}

export interface SolutionState {
    type: 'solution';
    solutions: Array<Solution>;
    answer: Solution | null;
}

export interface SolutionPerItemState {
    type: 'solutionPerItem';
    solutions: Record<string, Solution[]>;
    answers: Record<string, Solution> | null;
}

export interface ItemState {
    type: 'itemState';
    item: OrderItem;
    state: Array<QuestionState | SolutionState>;
}

export interface ElaborateState {
    type: 'elaborate';
    requireUpload: boolean;
    uploadDescription: string | null;
    requireDescription: boolean;
    descriptionDescription: string | null;
}

export type SupportCaseState =
    | RootQuestionsState
    | ItemsSelectionState
    | QuestionState
    | MultiItemQuestionState
    | SolutionState
    | SolutionPerItemState
    | ItemState
    | ElaborateState;

interface SupportCaseStore {
    bearer: string | null;
    supportCase: SupportCase | null;
    supportCases: SupportCaseListResponse | null;
    getSupportCase: (
        bearer: string,
        supportCaseIdentifier: string
    ) => Promise<void>;
    getSupportCases: (
        bearer: string,
        abortController?: AbortController
    ) => Promise<void>;
    createSupportCase: (
        bearer: string,
        abortController?: AbortController
    ) => Promise<void>;

    state: Array<SupportCaseState>;
    stateItems: Array<string>;
    setStateItems: (
        items: Array<{item: OrderItem; qty: null | number}>
    ) => Promise<void>;

    submitAnswer: (
        answer: Answer,
        itemIdentifier: string | null
    ) => Promise<void>;
    submitAnswerPerItem: (answers: Record<string, Answer>) => Promise<void>;
    submitSolution: (solution: Solution) => Promise<void>;
    submitSolutionPerItem: (answers: Record<string, Solution>) => Promise<void>;

    submitElaboration: (
        description: string | null,
        files: Array<File>
    ) => Promise<void>;

    goBack: (index: number) => void;
    reset: () => void;
}

const submitAnswerRequestFactory = (
    supportCaseId: string,
    answer: Answer,
    itemIdentifier: string | null,
    bearer: string
) => {
    return getClient(bearer)
        .url(
            `/after_sales/${
                itemIdentifier ? 'support_case_items' : 'support_cases'
            }/answer`
        )
        .post(
            itemIdentifier
                ? {
                      supportCaseId,
                      orderItemIdentifier: itemIdentifier,
                      answerId: answer.id,
                  }
                : {
                      supportCaseId,
                      answerId: answer.id,
                  }
        )
        .badRequest(res => {
            throw new Error('Ongeldige invoer.');
        })
        .error(429, () => {
            throw new Error('Te veel pogigingen, probeer het later nog eens.');
        })
        .internalError(res => {
            throw new Error(
                'Er is een onbekende fout opgetreden, probeer het later nog eens of neem contact met ons op.'
            );
        });
};

const submitItemsSelectionRequestFactory = (
    supportCaseId: string,
    itemIdentifier: string,
    qty: number | null,
    bearer: string
) => {
    return getClient(bearer)
        .url(`/after_sales/support_case_items/draft`)
        .post({
            supportCaseId,
            orderItemIdentifier: itemIdentifier,
            qty,
        })
        .badRequest(res => {
            throw new Error('Ongeldige invoer.');
        })
        .error(429, () => {
            throw new Error('Te veel pogigingen, probeer het later nog eens.');
        })
        .internalError(res => {
            throw new Error(
                'Er is een onbekende fout opgetreden, probeer het later nog eens of neem contact met ons op.'
            );
        });
};

const submitSolutionRequestFactory = (
    supportCaseId: string,
    solution: Solution,
    itemIdentifier: string | null,
    bearer: string
) => {
    const client = getClient(bearer).url(
        `/after_sales/${
            itemIdentifier ? 'support_case_items' : 'support_cases'
        }/solution`
    );

    return client
        .post(
            itemIdentifier
                ? {
                      supportCaseId,
                      solutionId: solution.id,
                      orderItemIdentifier: itemIdentifier,
                  }
                : {
                      supportCaseId,
                      solutionId: solution.id,
                  }
        )
        .badRequest(res => {
            throw new Error('Ongeldige invoer.');
        })
        .error(429, () => {
            throw new Error('Te veel pogigingen, probeer het later nog eens.');
        })
        .internalError(res => {
            throw new Error(
                'Er is een onbekende fout opgetreden, probeer het later nog eens of neem contact met ons op.'
            );
        });
};

const submitElaborationRequestFactory = (
    supportCaseId: string,
    description: string | null,
    files: Array<File>,
    bearer: string
) => {
    const formData = new FormData();
    formData.append('supportCaseId', supportCaseId);
    formData.append('description', description ?? '');

    for (const file of files) {
        formData.append('attachments[]', file, undefined);
    }

    return getClient(bearer, true)
        .addon(FormDataAddon)
        .url(`/after_sales/support_cases/elaboration`)
        .body(formData)
        .post()
        .badRequest(res => {
            throw new Error('Ongeldige invoer.');
        })
        .error(429, () => {
            throw new Error('Te veel pogigingen, probeer het later nog eens.');
        })
        .internalError(res => {
            throw new Error(
                'Er is een onbekende fout opgetreden, probeer het later nog eens of neem contact met ons op.'
            );
        });
};

export const useSupportCaseStore = create<SupportCaseStore>()((set, get) => ({
    bearer: null,
    supportCase: null,
    supportCases: null,
    getSupportCase: async (bearer: string, supportCaseIdentifier: string) => {
        await getClient(bearer)
            .url(`/after_sales/support_cases/${supportCaseIdentifier}/view`)
            .get()
            .error(401, () => {
                throw new Error(
                    'Ongeldige combinatie van ordernummer en e-mail adres.'
                );
            })
            .notFound(() => {
                throw new Error(
                    'Ongeldige combinatie van ordernummer en e-mail adres.'
                );
            })
            .badRequest(res => {
                throw new Error('Ongeldige invoer.');
            })
            .error(429, () => {
                throw new Error(
                    'Te veel pogigingen, probeer het later nog eens.'
                );
            })
            .internalError(res => {
                throw new Error(
                    'Er is een onbekende fout opgetreden, probeer het later nog eens of neem contact met ons op.'
                );
            })
            .json(res => {
                const data = res as unknown as {
                    identifier: string;
                    supportCase: SupportCase;
                };
                set({
                    supportCase: {
                        ...data.supportCase,
                    },
                    bearer,
                });
            });
    },
    getSupportCases: async (
        bearer: string,
        abortController?: AbortController
    ) => {
        await getClient(bearer)
            .addon(AbortAddon())
            .signal(abortController ?? new AbortController())
            .url(`/after_sales/support_cases/list`)
            .get()
            .unauthorized(() => {
                throw new AuthError(AuthErrorTypeEnum.NotFound);
            })
            .notFound(() => {
                throw new AuthError(AuthErrorTypeEnum.NotFound);
            })
            .badRequest(res => {
                throw new AuthError(AuthErrorTypeEnum.InputError);
            })
            .error(429, () => {
                throw new AuthError(AuthErrorTypeEnum.ToManyAttempts);
            })
            .internalError(res => {
                throw new AuthError(AuthErrorTypeEnum.Error);
            })
            .json(res => {
                const data = res as unknown as SupportCaseListResponse;
                set({
                    supportCases: data
                        .reverse()
                        .filter(
                            ({supportCase}) =>
                                supportCase.state !== 'pending_action'
                        ),
                    bearer,
                });
            });
    },
    createSupportCase: async (
        bearer: string,
        abortController?: AbortController
    ) => {
        await getClient(bearer)
            .addon(AbortAddon())
            .signal(abortController ?? new AbortController())
            .url('/after_sales/support_cases/draft')
            .post({})
            .unauthorized(() => {
                throw new AuthError(AuthErrorTypeEnum.NotFound);
            })
            .badRequest(res => {
                throw new AuthError(AuthErrorTypeEnum.InputError);
            })
            .error(429, () => {
                throw new AuthError(AuthErrorTypeEnum.ToManyAttempts);
            })
            .internalError(res => {
                throw new AuthError(AuthErrorTypeEnum.Error);
            })
            .json(res => {
                const data = res as unknown as {
                    supportCase: Omit<SupportCase, 'bearer'>;
                    questions: Array<Question>;
                };
                set({
                    supportCase: {
                        ...data.supportCase,
                    },
                    bearer,
                    state: [
                        {
                            type: 'rootQuestions',
                            questions: data.questions,
                            answer: null,
                        },
                    ],
                    stateItems: [],
                });
            });
    },

    state: [],
    stateItems: [],

    setStateItems: async (
        items: Array<{item: OrderItem; qty: null | number}>
    ) => {
        const supportCase = get().supportCase as SupportCase;
        const requests: Array<Promise<unknown>> = [];

        for (const item of items) {
            requests.push(
                submitItemsSelectionRequestFactory(
                    supportCase.identifier.value,
                    item.item.identifier.value,
                    item.qty,
                    get().bearer as string
                ).json()
            );
        }

        await Promise.allSettled(requests);

        const state = [...get().state];
        const nextQuestionState = state.pop() as
            | MultiItemQuestionState
            | SolutionState;
        const itemsState = state.pop() as ItemsSelectionState;
        itemsState.items = items.map(item => item.item);

        if (nextQuestionState.type !== 'multiItemQuestion') {
            set({
                stateItems: items.map(item => item.item.identifier.value),
            });
            return;
        }

        const questions: Record<string, Question> = {};

        for (const item of items) {
            questions[item.item.identifier.value] =
                nextQuestionState.questions['unknown'];
        }

        nextQuestionState.questions = questions;

        const nextState = [...state, itemsState, nextQuestionState];

        set({
            stateItems: items.map(item => item.item.identifier.value),
            state: nextState,
        });
    },

    submitAnswer: async (
        answer: Answer,
        itemIdentifier: string | null = null
    ) => {
        await submitAnswerRequestFactory(
            (get().supportCase as SupportCase).identifier.value,
            answer,
            null,
            get().bearer as string
        ).json(res => {
            const data = res as unknown as {
                supportCase: SupportCase;
                nextQuestion: Question | null;
                solutions: Array<Solution>;
            };
            const state = [...get().state];
            const currentState = state.pop() as
                | RootQuestionsState
                | QuestionState;

            currentState.answer = answer;

            const nextState = [...state, currentState];

            if (
                data.nextQuestion &&
                (data.nextQuestion.requireItems || data.nextQuestion.perItem) &&
                get().stateItems.length === 0
            ) {
                nextState.push({
                    type: 'itemsSelection',
                    items: [],
                    qtyRequired: true,
                });
            }

            if (
                !data.nextQuestion &&
                data.solutions &&
                data.solutions.filter(solution => solution.requireItems)
                    .length > 0 &&
                get().stateItems.length === 0
            ) {
                nextState.push({
                    type: 'itemsSelection',
                    items: [],
                    qtyRequired: false,
                });
            }

            if (data.nextQuestion && data.nextQuestion.perItem) {
                const questions: Record<string, Question> = {};
                for (const itemIdentifier of get().stateItems) {
                    questions[itemIdentifier] = data.nextQuestion as Question;
                }

                if (Object.keys(questions).length === 0) {
                    questions['unknown'] = data.nextQuestion as Question;
                }

                nextState.push({
                    type: 'multiItemQuestion',
                    questions,
                    answers: null,
                });
            }

            if (data.nextQuestion && !data.nextQuestion.perItem) {
                nextState.push({
                    type: 'question',
                    question: data.nextQuestion,
                    answer: null,
                });
            }

            if (data.solutions.length > 0) {
                nextState.push({
                    type: 'solution',
                    solutions: data.solutions,
                    answer: null,
                });
            }

            set({
                state: nextState,
            });
        });
    },
    submitAnswerPerItem: async (answers: Record<string, Answer>) => {
        const state = [...get().state];
        let currentState = state.pop() as
            | MultiItemQuestionState
            | SolutionPerItemState;

        const nextSolutionPerItemState: SolutionPerItemState = {
            type: 'solutionPerItem',
            solutions: {},
            answers: null,
        };

        if (currentState.type === 'solutionPerItem') {
            nextSolutionPerItemState.solutions = currentState.solutions;
            currentState = state.pop() as MultiItemQuestionState;
        }

        currentState.answers = answers;

        const nextState = [...state, currentState];
        const nextQuestionPerItemState: MultiItemQuestionState = {
            type: 'multiItemQuestion',
            questions: {},
            answers: null,
        };

        const supportCase = get().supportCase as SupportCase;

        for (const itemIdentifier in answers) {
            const answer = answers[itemIdentifier];
            const response = (await submitAnswerRequestFactory(
                supportCase.identifier.value,
                answer,
                itemIdentifier,
                get().bearer as string
            ).json()) as {
                supportCase: SupportCase;
                nextQuestion: Question | null;
                solutions: Array<Solution>;
            };

            if (response.nextQuestion) {
                nextQuestionPerItemState.questions[itemIdentifier] =
                    response.nextQuestion;

                continue;
            }

            if (response.solutions.length > 0) {
                nextSolutionPerItemState.solutions = {
                    ...nextSolutionPerItemState.solutions,
                    [itemIdentifier]: response.solutions,
                };
            }
        }

        if (Object.values(nextQuestionPerItemState.questions).length > 0) {
            nextState.push(nextQuestionPerItemState);
        }

        if (Object.values(nextSolutionPerItemState.solutions).length > 0) {
            nextState.push(nextSolutionPerItemState);
        }

        set({
            state: nextState,
        });
    },
    submitSolution: async (solution: Solution) => {
        await submitSolutionRequestFactory(
            (get().supportCase as SupportCase).identifier.value,
            solution,
            null,
            get().bearer as string
        ).json(res => {
            const response = res as unknown as {supportCase: SupportCase};
            const state = [...get().state];
            const currentState = state.pop() as SolutionState;

            currentState.answer = solution;

            const nextState = [...state, currentState];

            if (solution.requireDescription || solution.requireUpload) {
                nextState.push({
                    type: 'elaborate',
                    requireDescription: solution.requireDescription,
                    requireUpload: solution.requireUpload,
                    uploadDescription: solution.uploadDescription ?? null,
                    descriptionDescription:
                        solution.descriptionDescription ?? null,
                });

                set({
                    state: nextState,
                });

                return;
            }

            set({
                state: nextState,
                supportCase: response.supportCase,
            });
        });
        void get().getSupportCases(get().bearer as string);
    },
    submitSolutionPerItem: async (answers: Record<string, Solution>) => {
        let requireUpload = false;
        let requireDescription = false;
        let uploadDescription: null | string = null;
        let descriptionDescription: null | string = null;
        let supportCase = get().supportCase as SupportCase;

        for (const itemIdentifier in answers) {
            requireUpload =
                requireUpload || answers[itemIdentifier].requireUpload;
            requireDescription =
                requireDescription ||
                answers[itemIdentifier].requireDescription;
            uploadDescription =
                answers[itemIdentifier].uploadDescription ?? null;
            descriptionDescription =
                answers[itemIdentifier].descriptionDescription ?? null;

            await submitSolutionRequestFactory(
                supportCase.identifier.value,
                answers[itemIdentifier],
                itemIdentifier,
                get().bearer as string
                // eslint-disable-next-line no-loop-func
            ).json(res => {
                const response = res as unknown as {
                    supportCase: SupportCase;
                };
                const state = [...get().state];
                const currentState = state.pop() as SolutionPerItemState;

                currentState.answers = {
                    ...currentState.answers,
                    [itemIdentifier]: answers[itemIdentifier],
                };

                const nextState = [...state, currentState];
                supportCase = response.supportCase;

                set({
                    state: nextState,
                });
            });
        }

        const nextState = [...get().state];

        if (!requireUpload && !requireDescription) {
            set({
                supportCase: supportCase,
            });
            return;
        }

        nextState.push({
            type: 'elaborate',
            requireDescription,
            requireUpload,
            uploadDescription,
            descriptionDescription,
        });

        set({
            state: nextState,
        });

        void get().getSupportCases(get().bearer as string);
    },
    goBack: (index: number) => {
        const state = [...get().state];
        const nextState = state.slice(0, index + 1);
        const currentState = nextState.pop() as SupportCaseState;

        if (
            ('questions' in currentState || 'question' in currentState) &&
            !('answers' in currentState)
        ) {
            currentState.answer = null;
        }

        set({
            state: [...nextState, currentState],
        });
    },
    submitElaboration: async (
        description: string | null,
        files: Array<File>
    ) => {
        await submitElaborationRequestFactory(
            get().supportCase!.identifier.value,
            description,
            files,
            get().bearer as string
        ).json(res => {
            const response = res as unknown as {
                supportCase: SupportCase;
            };

            set({
                supportCase: response.supportCase,
            });
        });

        void get().getSupportCases(get().bearer as string);
    },
    reset: () => {
        const supportCase = get().supportCase;
        if (null !== supportCase && supportCase.state === 'draft') {
            void getClient(get().bearer as string)
                .url('/after_sales/support_cases/cancel')
                .post({
                    supportCaseId: supportCase.identifier.value,
                })
                .res()
                .catch(() => {});
        }

        set({
            supportCase: null,
            state: [],
            stateItems: [],
        });
    },
}));

export const getClient = (secret: string, omitContentType: boolean = false) => {
    const headers: Record<string, string> = {
        Accept: 'application/json',
        Authorization: `Basic ${secret}`,
    };

    if (!omitContentType) {
        headers['Content-Type'] = 'application/json';
    }

    return wretch('/api/v2')
        .options({
            headers,
            redirect: 'manual',
        })
        .errorType('json');
};
